import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { ObtenerPedidoSuccess } from "../../../services/store/compras";
import "./CheckoutSuccess.css";

const POLL_MS = 600;
const POLL_MAX = 28;
/** Refrescos tras el primer éxito (por si el webhook actualiza estado_pago). */
const REFRESH_DELAYS_MS = [2000, 4500];

function parsePedidoFromQuery(searchParams) {
  const q = searchParams.get("pedido_id") || searchParams.get("pedido");
  if (!q) return null;
  const n = Number(q);
  return Number.isInteger(n) && n > 0 ? String(n) : null;
}

function formatoEstadoPago(raw) {
  const e = String(raw || "").trim().toUpperCase();
  if (!e) {
    return { label: "Sin registrar", className: "is-muted" };
  }
  if (e === "PAGADO") {
    return { label: "Pagado", className: "is-success" };
  }
  if (e === "PENDIENTE") {
    return { label: "Pendiente de confirmación", className: "is-pending" };
  }
  if (e === "FALLIDO") {
    return { label: "Pago fallido", className: "is-error" };
  }
  if (e === "REEMBOLSADO") {
    return { label: "Reembolsado", className: "is-muted" };
  }
  return { label: String(raw), className: "is-muted" };
}

function CheckoutSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const confirmacion = location.state?.checkoutConfirmacion || null;

  const isPaggoReturn = useMemo(
    () => searchParams.get("gateway") === "paggo",
    [searchParams]
  );

  const queryPedido = parsePedidoFromQuery(searchParams);

  const grupoRef = useRef(
    typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem("grupo_reserva")
      : null
  );

  const pedidoIdSnapshotRef = useRef(
    typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem("pedido_id")
      : null
  );

  const [pedidoId, setPedidoId] = useState(() => {
    const fromState = confirmacion?.pedido_id;
    if (fromState != null && String(fromState).trim() !== "") {
      return String(fromState).trim();
    }

    try {
      const s = sessionStorage.getItem("pedido_id");
      if (s) return String(s).trim();
    } catch {
      //
    }

    return queryPedido || "";
  });

  const [noPo, setNoPo] = useState(() => {
    const fromState = confirmacion?.no_po;
    if (fromState != null && String(fromState).trim() !== "") {
      return String(fromState).trim();
    }

    const fromNav = location.state?.no_po;
    if (fromNav != null && String(fromNav).trim() !== "") {
      return String(fromNav).trim();
    }

    try {
      const raw = sessionStorage.getItem("pedido_no_po");
      return raw ? String(raw) : "";
    } catch {
      return "";
    }
  });

  const [estadoPago, setEstadoPago] = useState(null);
  const [pollPhase, setPollPhase] = useState(() => {
    if (confirmacion?.pedido_id) return "done";

    try {
      if (sessionStorage.getItem("pedido_id")) return "done";
    } catch {
      //
    }

    if (queryPedido) return "done";

    try {
      if (sessionStorage.getItem("grupo_reserva")) return "polling";
    } catch {
      //
    }

    return "idle";
  });

  const refreshTimersRef = useRef([]);

  useEffect(() => {
    let cancelled = false;

    const clearRefreshTimers = () => {
      refreshTimersRef.current.forEach((id) => window.clearTimeout(id));
      refreshTimersRef.current = [];
    };

    const applyPayload = (payload) => {
      if (!payload || payload.pedido_id == null) return false;
      setPedidoId(String(payload.pedido_id));
      setNoPo(payload.no_po != null ? String(payload.no_po) : "");
      setEstadoPago(
        payload.estado_pago != null ? String(payload.estado_pago) : null
      );
      setPollPhase("done");
      return true;
    };

    const fetchParamsForRefresh = (last) => {
      if (last.grupo) {
        return { grupo_reserva: last.grupo };
      }
      if (last.pedidoId) {
        return { pedido_id: last.pedidoId };
      }
      return null;
    };

    const run = async () => {
      const grupo = grupoRef.current ? String(grupoRef.current).trim() : "";
      const pedidoFallback =
        String(pedidoIdSnapshotRef.current || "").trim() ||
        String(queryPedido || "").trim() ||
        (confirmacion?.pedido_id != null
          ? String(confirmacion.pedido_id).trim()
          : "");

      const lastParamsRef = { grupo: grupo || null, pedidoId: null };

      const scheduleFollowUpRefreshes = () => {
        clearRefreshTimers();
        const params = fetchParamsForRefresh(lastParamsRef);
        if (!params) return;

        REFRESH_DELAYS_MS.forEach((delay) => {
          const id = window.setTimeout(async () => {
            if (cancelled) return;
            try {
              const res = await ObtenerPedidoSuccess(params);
              if (cancelled || !res?.pedido_id) return;
              setEstadoPago(
                res.estado_pago != null ? String(res.estado_pago) : null
              );
              setNoPo(res.no_po != null ? String(res.no_po) : "");
            } catch {
              //
            }
          }, delay);
          refreshTimersRef.current.push(id);
        });
      };

      if (grupo) {
        let attempts = 0;
        while (!cancelled && attempts < POLL_MAX) {
          try {
            const res = await ObtenerPedidoSuccess({ grupo_reserva: grupo });
            if (cancelled) return;
            if (res?.ok !== false && applyPayload(res)) {
              scheduleFollowUpRefreshes();
              return;
            }
          } catch {
            //
          }
          attempts += 1;
          await new Promise((r) => setTimeout(r, POLL_MS));
        }
        if (!cancelled) {
          setPollPhase("timeout");
        }
        return;
      }

      if (pedidoFallback) {
        try {
          const res = await ObtenerPedidoSuccess({
            pedido_id: pedidoFallback,
          });
          if (cancelled) return;
          if (res?.ok !== false && applyPayload(res)) {
            lastParamsRef.pedidoId = pedidoFallback;
            lastParamsRef.grupo = null;
            scheduleFollowUpRefreshes();
          }
        } catch {
          if (!cancelled) setPollPhase("timeout");
        }
        return;
      }

      if (!cancelled) {
        setPollPhase("timeout");
      }
    };

    run();

    return () => {
      cancelled = true;
      clearRefreshTimers();
    };
    // Efecto de arranque único (confirmación del pedido); deps intencionalmente vacías.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      localStorage.removeItem("cart");
      localStorage.removeItem("cart_expiration");
      localStorage.removeItem("art_expiration");
    } catch {
      //
    }

    try {
      sessionStorage.removeItem("grupo_reserva");
      sessionStorage.removeItem("checkout_subtotal");
      sessionStorage.removeItem("checkout_core_charge_total");
      sessionStorage.removeItem("checkout_grand_total");
      sessionStorage.removeItem("checkout_total");
      sessionStorage.removeItem("checkout_envio");
      sessionStorage.removeItem("checkout_pago");
      sessionStorage.removeItem("checkout_reserva_resumen");
      sessionStorage.removeItem("pedido_id");
      sessionStorage.removeItem("pedido_no_po");
    } catch {
      //
    }
  }, []);

  const showPedido = !!pedidoId;
  const showPolling = pollPhase === "polling" && !showPedido;
  const showTimeout = pollPhase === "timeout" && !showPedido;

  const estadoVisual = formatoEstadoPago(estadoPago);

  return (
    <div className="checkout-success-container">
      <div className="checkout-success-bg-glow checkout-success-bg-glow-top" />
      <div className="checkout-success-bg-glow checkout-success-bg-glow-left" />
      <div className="checkout-success-bg-glow checkout-success-bg-glow-right" />

      <div className="checkout-success-shell">
        <div className="checkout-success-card">
          <div className="checkout-success-top-line" />

          <div className="checkout-success-icon-wrap">
            <div className="checkout-success-cart-track">
              <div className="checkout-success-animated-cart">
                <span className="checkout-success-cart-handle" />
                <span className="checkout-success-cart-basket" />
                <span className="checkout-success-cart-wheel checkout-success-cart-wheel--left" />
                <span className="checkout-success-cart-wheel checkout-success-cart-wheel--right" />
              </div>
            </div>
          </div>

          <span className="checkout-success-badge">
            {isPaggoReturn ? "Pago recibido" : "Pago confirmado"}
          </span>

          <h1 className="checkout-success-title">
            Tu pago fue confirmado correctamente
          </h1>

          <p className="checkout-success-description">
            {isPaggoReturn
              ? "Hemos recibido la confirmación de tu pago. Tu orden ya fue registrada y continuará con su proceso correspondiente."
              : "Tu compra se registró exitosamente y el pago fue procesado correctamente. Ya puedes consultar el estado de tu pedido."}
          </p>

          <div className="checkout-success-order-hero">
            <span className="checkout-success-order-hero-label">
              Número de orden
            </span>
            <strong className="checkout-success-order-hero-id">
              {showPedido ? `#${pedidoId}` : "Confirmando..."}
            </strong>
          </div>

          {showPolling && (
            <div className="checkout-success-inline-note">
              Estamos obteniendo tu número de pedido...
            </div>
          )}

          {showTimeout && (
            <div className="checkout-success-inline-note checkout-success-inline-note-warning">
              Tu pago ya fue recibido. El número de pedido aparecerá en breve dentro de
              <strong> Mis pedidos</strong>.
            </div>
          )}

          <div
            className={`checkout-success-summary ${
              !noPo ? "checkout-success-summary-single" : ""
            }`}
          >
            {noPo ? (
              <div className="checkout-success-summary-box">
                <span className="label">No. PO</span>
                <strong>{noPo}</strong>
              </div>
            ) : null}

            <div className="checkout-success-summary-box checkout-success-summary-box-status">
              <span className="label">Estado del pago</span>
              <strong
                className={`checkout-success-estado-pago ${estadoVisual.className}`}
              >
                {showPedido ? estadoVisual.label : "Consultando..."}
              </strong>
            </div>
          </div>

          <div className="checkout-success-note">
            Puedes revisar los detalles desde tu cuenta o volver a la tienda para
            seguir comprando.
          </div>

          <div className="checkout-success-actions">
            <button
              className="checkout-success-btn checkout-success-btn-secondary"
              onClick={() =>
                navigate("/cliente/account", {
                  state: { mode: "login", redirectTo: "/cliente/login" },
                })
              }
            >
              Ver mis pedidos
            </button>

            <button
              className="checkout-success-btn checkout-success-btn-primary"
              onClick={() => navigate("/store/inicio")}
            >
              Volver a la tienda
            </button>
          </div>
        </div>

        <div className="checkout-success-footer">cyravi by Nexus Bridge</div>
      </div>
    </div>
  );
}

export default CheckoutSuccess;
